import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Flex from "../../../components/Layout/Flex";
import { ConsultantSalary, SalaryByTypeEntries } from "../../../models/Salary";
import { formatCurrencyString } from "../../../utils/string.utils";
import AppText from "../../Core/AppText/AppText";

interface SalaryJournalConsultantsProps {
	consultant: ConsultantSalary;
}
interface SalaryRowProps {
	label: string;
	quantity: number | string;
	unitPrice: number | string;
	total: number | string;
	className?: string;
}

const SalaryJournalConsultants: React.FC<SalaryJournalConsultantsProps> = ({
	consultant: {
		id,
		firstName,
		lastName,
		profile: { employeeNumber, socialSecurityNumber },
		salary: {
			salaryTypes: {
				hourly,
				deduction,
				sickPay,
				sickPayNonPaid,
				qualifyingDeduction,
				leaveOfAbsence,
				childCare,
				holidayPay,
				ob1,
				ob2,
				ob3,
				ob4,
				ob5,
			},
			grossSalary,
		},
	},
}) => {
	const { t } = useTranslation();

	const SalaryRow: React.FC<SalaryRowProps> = ({
		label,
		quantity,
		unitPrice,
		total,
		className,
	}) => {
		return (
			<tr className={className}>
				<td>{label}</td>
				<td className="right-align">
					{typeof quantity === "number" && quantity > 0
						? quantity.toFixed(2).replace(".", ",")
						: ""}
				</td>
				<td className="right-align">
					{typeof unitPrice === "number" ? formatPrice(unitPrice) : ""}
				</td>
				<td className="right-align">{typeof total === "number" ? formatPrice(total) : ""}</td>
			</tr>
		);
	};

	const hourlyPayGroupedByPrice = useMemo(() => {
		return groupEntriesByPrice(hourly.entries);
	}, [hourly]);

	const sickPayGroupedByPrice = useMemo(() => {
		return groupEntriesByPrice(sickPay.entries);
	}, [sickPay]);

	const renderHourlyPayments = () => {
		return Array.from(hourlyPayGroupedByPrice.values()).map((row) => (
			<SalaryRow
				key={`${id}-hourly-${row.price}`}
				label={t("salary.journal.salaryTypes.hourly")}
				quantity={row.quantity}
				unitPrice={row.price}
				total={row.total}
			/>
		));
	};

	const renderObPayments = () => {
		const obTypes = [
			{ data: ob1, label: "ob1" },
			{ data: ob2, label: "ob2" },
			{ data: ob3, label: "ob3" },
			{ data: ob4, label: "ob4" },
			{ data: ob5, label: "ob5" },
		];

		return obTypes.flatMap(({ data, label }) => {
			if (!data || data.total === 0) return [];
			const obGrouped = groupEntriesByPrice(data.entries);
			return Array.from(obGrouped.values()).map((row) => (
				<SalaryRow
					key={`${id}-${label}-${row.price}`}
					label={t(`salary.journal.salaryTypes.${label}`)}
					quantity={row.quantity}
					unitPrice={row.price}
					total={row.total}
				/>
			));
		});
	};

	const renderSickPayments = () => {
		const arr = Array.from(sickPayGroupedByPrice.values());

		if (arr.length === 0) {
			return (
				<SalaryRow
					label={t("salary.journal.salaryTypes.sickPay")}
					quantity={""}
					unitPrice={""}
					total={""}
				/>
			);
		}

		return arr.map((row) => (
			<SalaryRow
				key={`${id}-sickPay-${row.price}`}
				label={t("salary.journal.salaryTypes.sickPay")}
				quantity={row.quantity}
				unitPrice={row.price}
				total={row.total}
			/>
		));
	};

	return (
		<Flex direction="column" margin="0 0 70px 0" key={id}>
			<Flex direction="row" margin="0 0 7px 0">
				<AppText size="l" color="black">
					{employeeNumber}, &nbsp;
				</AppText>
				<AppText size="l" color="black">
					{socialSecurityNumber}
				</AppText>
			</Flex>

			<StyledTable>
				<thead>
					<tr>
						<th className="full-name headings-cell">
							{firstName} {lastName}
						</th>
						<th className="headings-cell right-align">{t("salary.journal.quantity")}</th>
						<th className="headings-cell right-align">{t("salary.journal.award")}</th>
						<th className="headings-cell right-align">{t("salary.journal.amount")}</th>
					</tr>
				</thead>

				<tbody>
					{/* hourly */}
					{renderHourlyPayments()}

					{/* OB supplements */}
					{renderObPayments()}

					{/* holidayPay */}
					{holidayPay && (
						<SalaryRow
							label={t("salary.journal.salaryTypes.holidayPay")}
							quantity={(holidayPay?.entries || []).reduce((tot, e) => {
								return tot + e.quantity;
							}, 0)}
							unitPrice={""}
							total={holidayPay.total > 0 ? holidayPay.total : ""}
						/>
					)}

					{/* sickPay */}
					{renderSickPayments()}

					{/* sickPayNonPaid */}
					<SalaryRow
						label={t("salary.journal.salaryTypes.sickPayNonPaid")}
						quantity={sickPayNonPaid.entries.reduce((tot, e) => {
							return tot + e.quantity;
						}, 0)}
						unitPrice={""}
						total={sickPayNonPaid.total > 0 ? sickPayNonPaid.total : ""}
					/>

					{/* qualifyingDeduction */}
					<SalaryRow
						label={t("salary.journal.salaryTypes.qualifyingDeduction")}
						quantity={qualifyingDeduction.total > 0 ? 1 : ""}
						unitPrice={""}
						total={qualifyingDeduction.total > 0 ? qualifyingDeduction.total * -1 : ""}
					/>

					{/* leaveOfAbsence */}
					<SalaryRow
						label={t("salary.journal.salaryTypes.leaveOfAbsence")}
						quantity={leaveOfAbsence.entries.reduce((tot, e) => {
							return tot + e.quantity;
						}, 0)}
						unitPrice={""}
						total={leaveOfAbsence.total > 0 ? leaveOfAbsence.total : ""}
					/>

					{/* childCare */}
					<SalaryRow
						label={t("salary.journal.salaryTypes.childCare")}
						quantity={childCare.entries.reduce((tot, e) => {
							return tot + e.quantity;
						}, 0)}
						unitPrice={""}
						total={""}
					/>

					{/* deduction */}
					<SalaryRow
						label={t("salary.journal.salaryTypes.deduction")}
						quantity={deduction.total > 0 ? 1 : ""}
						unitPrice={""}
						total={deduction.total > 0 ? deduction.total * -1 : ""}
					/>

					<tr>
						<td colSpan={4} className="right-align bold-text gross-salary">
							<AppText size="l" color="black" weight="medium" textAlign="right">
								{formatPrice(grossSalary)}
							</AppText>
						</td>
					</tr>
				</tbody>
			</StyledTable>
		</Flex>
	);
};

export default SalaryJournalConsultants;

function formatPrice(num: number) {
	return formatCurrencyString(num, { maximumFractionDigits: 2 });
}

const StyledTable = styled.table`
	tbody td {
		padding: 0 0 5px 0;
	}
`;

function groupEntriesByPrice(entries: SalaryByTypeEntries[]) {
	const entriesByPrice = new Map<
		number,
		{ total: number; price: number; quantity: 0; entries: SalaryByTypeEntries[] }
	>();

	for (const entry of entries) {
		let e = entriesByPrice.get(entry.price);

		if (!e) {
			e = entriesByPrice
				.set(entry.price, { total: 0, price: entry.price, quantity: 0, entries: [] })
				.get(entry.price);
		}

		e!.entries.push(entry);
		e!.total += entry.price * entry.quantity;
		e!.quantity += entry.quantity;
	}

	return entriesByPrice;
}
